import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, CalendarHeart } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import Pillars from "@/components/Pillars";
import HowItWorksSteps from "@/components/HowItWorksSteps";
import CurrentRead from "@/components/CurrentRead";
import EmailCaptureCTA from "@/components/EmailCaptureCTA";

const HERO_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_the-literary-room/artifacts/og6ye9dn_image.png";

export default function Home() {
  const [content, setContent] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [gatherings, setGatherings] = useState([]);
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get("/content/home").then(({ data }) => setContent(data || {}));
    api.get("/books", { params: { category: "upcoming" } }).then(({ data }) => setUpcoming(data || []));
    api.get("/events").then(({ data }) => setGatherings((data || []).filter((e) => (e.date || "") >= today).slice(0, 3)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden grain">
        <div className="absolute inset-0 bg-gradient-to-br from-mahogany-950 via-mahogany-900 to-mahogany-800" />
        <div className="absolute right-0 top-0 w-full md:w-1/2 h-full opacity-60 md:opacity-100">
          <img src={HERO_IMG} alt="A woman reading in a warm lounge" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-mahogany-950 via-mahogany-950/70 md:via-mahogany-950/30 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <Reveal className="max-w-2xl">
            <p className="font-script text-3xl sm:text-4xl text-fuchsia mb-3">This is your room.</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl font-light text-cream leading-[0.95] tracking-tight">
              {content.hero_headline_1 || "GOOD BOOKS."}<br />
              {content.hero_headline_2 || "DOPE WOMEN."}<br />
              <span className="text-fuchsia">{content.hero_headline_3 || "REAL CONVERSATIONS."}</span>
            </h1>
            <p className="text-cream/75 text-lg mt-7 max-w-xl leading-relaxed font-light">
              {content.hero_subtitle || "A curated literary community created for Black women to read, connect, grow, and build lifelong sisterhood."}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Link to="/join" className="btn-primary" data-testid="hero-join-btn">Join The Room</Link>
              <Link to="/about" className="btn-secondary" data-testid="hero-learn-btn">Learn More</Link>
            </div>
            <p className="font-script text-2xl text-gold mt-6">Free to Join</p>
          </Reveal>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-28 bg-mahogany-900" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-3xl mb-16">
            <p className="overline mb-4">Our Mission</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-cream leading-tight mb-6">
              This Is More Than a Book Club.<br /><span className="text-fuchsia">This Is Your Room.</span>
            </h2>
            <p className="text-cream/70 text-lg leading-relaxed font-light">
              {content.mission_body || "The Mahogany Room was created to give Black women an intentional space centered around literary exploration, meaningful conversation, sisterhood, personal growth, culture, community, and beautiful shared experiences."}
            </p>
          </Reveal>
          <Pillars />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-28 bg-gradient-to-b from-mahogany-950 to-mahogany-900" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-16">
            <p className="overline mb-4">The Experience</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-cream">How It Works</h2>
          </Reveal>
          <HowItWorksSteps />
        </div>
      </section>

      {/* CURRENT READ */}
      <section className="py-28 bg-mahogany-900" data-testid="current-read-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-14">
            <p className="overline mb-4">Now Reading</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-cream">The Current Read</h2>
          </Reveal>
          <Reveal><CurrentRead /></Reveal>
        </div>
      </section>

      {/* UPCOMING READS + GATHERINGS */}
      <section className="py-28 bg-cream" data-testid="upcoming-section" data-navbg="light">
        <div className={`mx-auto px-6 grid gap-16 items-start ${gatherings.length ? "max-w-7xl lg:grid-cols-2" : "max-w-4xl lg:grid-cols-1"}`}>
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-mahogany-900 mb-8">Upcoming Reads</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              {upcoming.slice(0, 3).map((b) => (
                <div key={b.id} data-testid={`upcoming-book-${b.id}`}>
                  <img src={b.cover_url} alt={b.title} className="w-full rounded-sm shadow-lg mb-3" />
                  <p className="font-serif text-lg text-mahogany-900 leading-tight">{b.title}</p>
                  <p className="text-mahogany-600 text-sm">{b.author}</p>
                </div>
              ))}
            </div>
            <Link to="/books" className="inline-flex items-center gap-2 text-fuchsia uppercase tracking-wider text-sm mt-8 hover:gap-3 transition-all" data-testid="view-all-books">
              View All Books <ArrowRight size={16} />
            </Link>
          </div>
          {gatherings.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-fuchsia mb-4">Coming Up</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light text-mahogany-900 mb-8">Upcoming Gatherings</h2>
              <div className="space-y-5">
                {gatherings.map((e) => {
                  const d = new Date(`${e.date}T00:00:00`);
                  const month = isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "long" });
                  const short = isNaN(d) ? e.date : `${d.getMonth() + 1}.${d.getDate()}.${String(d.getFullYear()).slice(2)}`;
                  return (
                    <div key={e.id} className="flex items-center gap-5 bg-white/60 border border-mahogany-700/10 rounded-sm p-5" data-testid={`gathering-${e.id}`}>
                      <div className="w-12 h-12 rounded-full bg-mahogany-900 flex items-center justify-center shrink-0">
                        <CalendarHeart size={20} className="text-gold" strokeWidth={1.25} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-wider text-fuchsia font-semibold">{month}</p>
                        <p className="font-serif text-xl text-mahogany-900 leading-tight">{e.title}</p>
                      </div>
                      <span className="text-mahogany-600 text-sm shrink-0">{short}</span>
                    </div>
                  );
                })}
              </div>
              <Link to="/events" className="inline-flex items-center gap-2 text-fuchsia uppercase tracking-wider text-sm mt-8 hover:gap-3 transition-all" data-testid="view-all-meetups">
                View All Meetups <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </section>

      <EmailCaptureCTA />
    </div>
  );
}
